import type { MapGeoJSONFeature } from 'maplibre-gl';
import { parseGeometryLocation, parseValidPosition, type Position } from '#lib/featureGeometry.js';

/**
 * External map tools offering a "drop a pin here" / street-level view.
 * Add a new entry here to surface it in the {@link StreetViewButton} chooser —
 * no component changes required.
 */
export type StreetViewProvider = 'google' | 'bing';

type StreetViewProviderDef = {
	id: StreetViewProvider;
	label: string;
	/** Build the external URL for a feature's `[lng, lat]` position. */
	buildUrl: (lng: number, lat: number) => string;
};

export const STREET_VIEW_PROVIDERS: StreetViewProviderDef[] = [
	{
		id: 'google',
		label: 'Google Street View',
		buildUrl: (lng, lat) =>
			`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`
	},
	{
		id: 'bing',
		label: 'Bing Maps',
		buildUrl: (lng, lat) => `https://www.bing.com/maps/search?style=3d&cp=${lat}~${lng}`
	}
];

function parseDataPosition(position: string): Position {
	return position.split(',').map(parseFloat).reverse() as Position;
}

/**
 * Position to centre a street-view link on. Prefers the feature's
 * `start_position` property (a `"lat,lng"` string), falling back to the first
 * vertex of the geometry. Returns null when neither yields a valid `[lng, lat]`.
 */
export function getStreetViewPosition(feature: MapGeoJSONFeature): NonNullable<Position> | null {
	const location = feature.properties.start_position
		? parseDataPosition(feature.properties.start_position as string)
		: parseGeometryLocation(feature.geometry);
	return parseValidPosition(location);
}
